import { styled } from '@linaria/react';
import { Flex, ScrollArea } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChartLegendItemWithCheckbox } from '../../../../../../components/line-chart/line-chart-legend-item-with-checkbox';
import { resolvePhaseLabels } from '../../../../../../helpers/phase-labels/phase-labels';
import { useFlowByPhaseTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { toggleFlowByPhasePhaseVisibility } from '../../../../../../store/strategy-store/strategy-store.actions';
import { getPhasesMetadata } from '../../flow-by-phase-tab.constants';

type FlowByPhaseChartLegendProps = {
  allPhaseMetadata?: Array<{ id: string; label: string; color: string; description: string }>;
  useGroups?: boolean;
  selectedSubGroups?: Set<string>;
  onSubGroupToggle?: (id: string, checked: boolean) => void;
};

export const FlowByPhaseChartLegend = ({
  allPhaseMetadata,
  useGroups = true,
  selectedSubGroups,
  onSubGroupToggle,
}: FlowByPhaseChartLegendProps) => {
  const { t } = useTranslation();
  const visiblePhases = useFlowByPhaseTabStore((state) => state.visiblePhases);
  const phases = useFlowByPhaseTabStore((state) => state.phases);
  const resolvedLabels = useMemo(() => resolvePhaseLabels(phases ?? undefined, t), [phases, t]);
  const phasesMetadata = useMemo(() => getPhasesMetadata(t, resolvedLabels), [t, resolvedLabels]);

  // Ensure required props are present in subgroup mode
  if (!useGroups && (!selectedSubGroups || !onSubGroupToggle)) {
    console.warn('FlowByPhaseChartLegend: selectedSubGroups and onSubGroupToggle are required when useGroups is false');
    return null;
  }

  // Use provided metadata or default to group metadata
  const displayMetadata = allPhaseMetadata || phasesMetadata;

  const legendItems = displayMetadata.map((phase) => {
    const isChecked = useGroups ? visiblePhases[phase.id] : (selectedSubGroups?.has(phase.id) ?? false);
    const handleToggle = useGroups
      ? () => toggleFlowByPhasePhaseVisibility(phase.id)
      : () => onSubGroupToggle?.(phase.id, !isChecked);

    return (
      <LegendItemWrapper key={`legend-item-${phase.id}`}>
        <LineChartLegendItemWithCheckbox
          value={phase.id}
          label={phase.label}
          color={phase.color}
          isChecked={isChecked}
          onToggle={handleToggle}
        />
      </LegendItemWrapper>
    );
  });

  // For subgroups with many items, use horizontal scroll
  if (!useGroups && displayMetadata.length > 10) {
    return (
      <ScrollArea type="auto" offsetScrollbars="x" scrollbarSize={8} w="100%" h="100%">
        <Flex justify="center" align="center" gap="md" h="100%" wrap="nowrap">
          {legendItems}
        </Flex>
      </ScrollArea>
    );
  }

  // For groups or few subgroups, use centered wrapping layout
  return (
    <Flex justify="center" align="center" gap="md" h="100%" wrap="wrap">
      {legendItems}
    </Flex>
  );
};

const LegendItemWrapper = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  min-width: fit-content;
  white-space: nowrap;
`;
